/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { Typography2 } from '@coursera/cds-core';
import { DegreeIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { getUpsellCopy } from 'bundles/stackability-common';
import DegreesModalCta from 'bundles/unified-description-page-common/components/modules/credits/DegreesModalCta';
import LearnMoreLink from 'bundles/unified-description-page-common/components/shared/LearnMoreLink';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { CREDITS_ANCHOR_ID } from 'bundles/unified-description-page-common/constants';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { getProductVariant } from 'bundles/unified-description-page-common/module-renderers/eligible-degrees/ConsumerEligibleDegreesModuleRenderer';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type {
  DegreeFragment,
  PartnerFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { formatPageDataDegrees } from 'bundles/unified-description-page-common/utils/formatPageDataDegrees';
import getEligibleDegreesDescription from 'bundles/unified-description-page-common/utils/getEligibleDegreesDescription';
import getEligibleDegreesTitle from 'bundles/unified-description-page-common/utils/getEligibleDegreesTitle';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import isForCreditNonDegree from 'bundles/unified-description-page-common/utils/isForCreditNonDegree';

import ACELogo from 'images/unified-description-page/pathways-ace-logo.png';

import _t from 'i18n!nls/xdp';

type Props = {
  degrees?: DegreeFragment[] | null;
  partners?: PartnerFragment[] | null;
  slug?: string;
  productTags?: ConsumerSpecializationPageFieldsFragment['productTags'];
  productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
};

const styles = {
  item: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  iconContainer: css`
    border-radius: 8px;
    background: #f4f7fa;
    display: flex;
    width: 76px;
    height: 54px;
    padding: calc(var(--cds-spacing-100) / 2);
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  `,
  description: css`
    max-width: 608px;
    padding-top: var(--cds-spacing-100);
  `,
  aceSection: css`
    display: flex;
    padding-top: var(--cds-spacing-150);
    gap: var(--cds-spacing-100);
  `,
};

const EligibleDegreesItem = ({ degrees, partners, slug, productTags, productVariant }: Props) => {
  const hasDegrees = degrees && degrees.length > 0;
  const hasACELink = !!productTags?.linkToACECreditRecommendation;
  const eventingProductData = useEventingProductData();
  const track = useTracker();

  if (!hasDegrees && !hasACELink) {
    return null;
  }

  const eligibleDegreesTitle = hasDegrees
    ? getEligibleDegreesTitle(degrees ?? [])
    : _t('Get a head start on your degree');

  const isFCND = !!slug && isForCreditNonDegree(slug, partners?.[0]?.shortName || '');
  const variantTitle = getVariantTitle(productVariant);
  const upsellCopy = getUpsellCopy().homepagesNavXdp;

  const { description: eligibleDegreesDescription, disclaimer: eligibleDegreesDisclaimer } =
    getEligibleDegreesDescription(
      variantTitle,
      getProductVariant({
        primarySpecialization: undefined,
        productVariant,
      }),
      degrees?.[0]?.upsellPartners?.[0]?.name,
      degrees?.[0]?.upsellType,
      isFCND,
      partners?.[0]?.name
    );

  return (
    <Section anchorId={CREDITS_ANCHOR_ID} spacing={0}>
      <div css={styles.item}>
        <div css={styles.iconContainer}>
          <DegreeIcon size="large" color="interactive" width={34} height={34} />
        </div>
        <div>
          <Typography2 component="h3" variant="subtitleLarge">
            {eligibleDegreesTitle}
          </Typography2>
          <div css={styles.description}>
            <Typography2 component="p" variant="bodySecondary" display="inline">
              {eligibleDegreesDescription}
            </Typography2>
            &nbsp;
            <DegreesModalCta
              modalDescription={<Typography2 component="p">{eligibleDegreesDescription}</Typography2>}
              descriptionDisclaimer={eligibleDegreesDisclaimer}
              modalTitle={eligibleDegreesTitle}
              degrees={formatPageDataDegrees(degrees ?? [])}
              inlineCta
            />
          </div>
          {hasACELink && (
            <div css={styles.aceSection}>
              <Imgix src={ACELogo} alt={_t('ACE Logo')} maxWidth={31} maxHeight={31} height={31} width={31} />
              <Typography2 component="p" variant="bodySecondary">
                <span>{upsellCopy.variantAceCreditRecommended(variantTitle)}</span>
                &nbsp;
                <LearnMoreLink
                  // @ts-expect-error URL: string
                  href={productTags?.linkToACECreditRecommendation}
                  trackingName="ace_credit_learn_more"
                  ariaLabel={_t('Learn more about ACE Recommendation')}
                  target="_blank"
                  onClick={() => {
                    track('click_button', {
                      button: {
                        name: 'description_page_ace',
                      },
                      pageSection: {
                        sectionName: 'eligible_degree',
                      },
                      ...eventingProductData,
                    });
                  }}
                />
              </Typography2>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default EligibleDegreesItem;
